// Subscription service — wraps planService + local cache in IndexedDB

import {
  SubscriptionTier,
  TIER_LIMITS,
  type TierLimits,
} from "../constants/subscriptionLimits";
import { storageService } from "./storageService";

export type SubscriptionStatus =
  | "active"
  | "cancelled"
  | "expired"
  | "trialing";

interface Subscription {
  userId: string;
  tier: SubscriptionTier;
  status: SubscriptionStatus;
  currentPeriodStart: string;
  currentPeriodEnd: string;
  stripeSubscriptionId?: string;
  cancelAtPeriodEnd: boolean;
  updatedAt: string;
}

const STORE_SUBSCRIPTION = "subscription";

const defaultSubscription = (userId: string): Subscription => ({
  userId,
  tier: "free",
  status: "active",
  currentPeriodStart: new Date().toISOString(),
  currentPeriodEnd: new Date(
    Date.now() + 30 * 24 * 60 * 60 * 1000,
  ).toISOString(),
  cancelAtPeriodEnd: false,
  updatedAt: new Date().toISOString(),
});

async function openDB(): Promise<IDBDatabase> {
  return storageService.openDB();
}

export const subscriptionService = {
  async getSubscription(userId: string): Promise<Subscription> {
    try {
      const db = await openDB();
      return new Promise((resolve) => {
        try {
          if (!db.objectStoreNames.contains(STORE_SUBSCRIPTION)) {
            resolve(defaultSubscription(userId));
            return;
          }
          const tx = db.transaction(STORE_SUBSCRIPTION, "readonly");
          const request = tx.objectStore(STORE_SUBSCRIPTION).get(userId);
          request.onsuccess = () => {
            const sub = request.result as Subscription | undefined;
            resolve(sub || defaultSubscription(userId));
          };
          request.onerror = () => resolve(defaultSubscription(userId));
        } catch {
          resolve(defaultSubscription(userId));
        }
      });
    } catch {
      return defaultSubscription(userId);
    }
  },

  getTierLimits(tier: SubscriptionTier): TierLimits {
    return TIER_LIMITS[tier];
  },

  async canUseFeature(
    userId: string,
    feature: "ai_search" | "ai_analysis" | "export",
  ): Promise<boolean> {
    const sub = await this.getSubscription(userId);
    const limits = this.getTierLimits(sub.tier);
    switch (feature) {
      case "ai_search":
        return limits.aiSearches === -1 || limits.aiSearches > 0;
      case "ai_analysis":
        return limits.aiAnalyses === -1 || limits.aiAnalyses > 0;
      case "export":
        return limits.exports === -1 || limits.exports > 0;
      default:
        return false;
    }
  },

  async updateSubscription(
    userId: string,
    subscription: Subscription,
  ): Promise<void> {
    try {
      const db = await openDB();
      if (!db.objectStoreNames.contains(STORE_SUBSCRIPTION)) return;
      const tx = db.transaction(STORE_SUBSCRIPTION, "readwrite");
      await new Promise<void>((resolve, reject) => {
        const request = tx.objectStore(STORE_SUBSCRIPTION).put(subscription);
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      console.warn("Failed to update subscription:", error);
    }
  },

  async getUpgradeUrl(
    userId: string,
    tier: SubscriptionTier,
    period: "monthly" | "yearly" = "monthly",
    options?: { earlyAdopter?: boolean },
  ): Promise<string> {
    if (tier === "free") throw new Error("Cannot upgrade to free tier");

    try {
      const { paymentService } = await import("./paymentService");
      const { checkoutUrl } = await paymentService.createCheckoutSession({
        userId,
        tier: tier as "pro" | "power",
        period,
        // First 200 Pro subscribers get $4.99/mo (or $49.99/yr); backend applies coupon when slots remain
        earlyAdopter: options?.earlyAdopter ?? (tier === "pro"),
      });
      return checkoutUrl;
    } catch (error: any) {
      throw new Error(error.message || "Failed to create checkout session");
    }
  },

  async syncSubscriptionFromBackend(
    userId: string,
    data: {
      tier: SubscriptionTier;
      status: SubscriptionStatus;
      stripeSubscriptionId?: string;
      currentPeriodStart: string;
      currentPeriodEnd: string;
      cancelAtPeriodEnd?: boolean;
    },
  ): Promise<void> {
    await this.updateSubscription(userId, {
      userId,
      ...data,
      cancelAtPeriodEnd: data.cancelAtPeriodEnd || false,
      updatedAt: new Date().toISOString(),
    });
  },
};
